import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import { getBusinesses } from '@/services/api';
import { Card, CardBody } from '@/components/common/Card';
import DataTable from '@/components/common/DataTable';
import SearchInput from '@/components/common/SearchInput';
import Select from '@/components/common/Select';
import { TierBadge, StatusBadge } from '@/components/common/Badge';
import { formatDate } from '@/utils/formatters';
import type { BusinessWithSubscription } from '@/types/business';

export default function Businesses() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [tierFilter, setTierFilter] = useState('all');
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ['businesses', page, search, tierFilter],
    queryFn: () => getBusinesses(page, 20, search || undefined, tierFilter),
  });

  const columns = useMemo<ColumnDef<BusinessWithSubscription>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Business',
        cell: ({ row }) => (
          <div className="flex items-center gap-3">
            {row.original.logo_url ? (
              <img
                src={row.original.logo_url}
                alt={row.original.name}
                className="w-10 h-10 rounded-lg object-cover"
              />
            ) : (
              <div className="w-10 h-10 rounded-lg bg-gray-200 flex items-center justify-center text-sm font-medium text-gray-600">
                {row.original.name[0].toUpperCase()}
              </div>
            )}
            <div>
              <p className="font-medium text-gray-900">{row.original.name}</p>
              <p className="text-xs text-gray-500">{row.original.industry || 'No industry'}</p>
            </div>
          </div>
        ),
      },
      {
        accessorKey: 'owner',
        header: 'Owner',
        cell: ({ row }) => row.original.owner?.full_name || row.original.owner?.email || '-',
      },
      {
        accessorKey: 'tier',
        header: 'Tier',
        cell: ({ row }) => <TierBadge tier={row.original.tier} />,
      },
      {
        accessorKey: 'location',
        header: 'Location',
        cell: ({ row }) => {
          const city = row.original.city;
          const state = row.original.state;
          if (!city && !state) return '-';
          return `${city || ''}${city && state ? ', ' : ''}${state || ''}`;
        },
      },
      {
        accessorKey: 'moderation_status',
        header: 'Status',
        cell: ({ row }) => <StatusBadge status={row.original.moderation_status || 'pending'} />,
      },
      {
        accessorKey: 'subscription',
        header: 'Subscription',
        cell: ({ row }) => {
          const sub = row.original.subscription;
          if (!sub) return <span className="text-gray-400">-</span>;
          return <StatusBadge status={sub.status} />;
        },
      },
      {
        accessorKey: 'created_at',
        header: 'Created',
        cell: ({ row }) => formatDate(row.original.created_at),
      },
    ],
    []
  );

  const tierOptions = [
    { value: 'all', label: 'All Tiers' },
    { value: 'free', label: 'Free' },
    { value: 'starter', label: 'Starter' },
    { value: 'professional', label: 'Professional' },
    { value: 'enterprise', label: 'Enterprise' },
  ];

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Businesses</h1>
        <p className="mt-1 text-sm text-gray-500">
          Manage business profiles and subscriptions
        </p>
      </div>

      {/* Filters */}
      <Card>
        <CardBody padding="sm">
          <div className="flex flex-col sm:flex-row gap-4">
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder="Search by business name..."
              className="flex-1"
            />
            <Select
              value={tierFilter}
              onChange={setTierFilter}
              options={tierOptions}
              className="w-full sm:w-48"
            />
          </div>
        </CardBody>
      </Card>

      {/* Businesses table */}
      <Card>
        <CardBody padding="none">
          <DataTable
            data={data?.businesses || []}
            columns={columns}
            isLoading={isLoading}
            page={page}
            pageSize={20}
            totalCount={data?.total}
            onPageChange={setPage}
            onRowClick={(business) => navigate(`/businesses/${business.business_id}`)}
          />
        </CardBody>
      </Card>
    </div>
  );
}
